import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, ShieldCheck, Upload, MapPin } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { AuditChip } from '@/components/audit/AuditChip';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';

import { BrandForm } from '@/features/phase2/BrandForm';
import { BrandLocationForm } from '@/features/phase2/BrandLocationForm';
import { CsvImportDialog } from '@/features/phase2/CsvImportDialog';

import { useApiError } from '@/lib/useApiError';
import {
  useBulkImportBrands,
  useDecideBrandProposal,
  useModerateBrandReview,
  useStaleBrands,
  useVerifyBrand,
} from '@/queries/phase2';
import {
  BRAND_BULK_BODY_BYTES_CAP,
  BRAND_BULK_ROW_CAP,
  brandUpsertSchema,
  type StaleBrand,
} from '@/schemas/admin/phase2/brands';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_authed/phase2/brands')({
  component: BrandsScreen,
});

function BrandsScreen() {
  const { t } = useTranslation('phase2');
  const stale = useStaleBrands();
  const verify = useVerifyBrand();
  const bulk = useBulkImportBrands();
  const resolveError = useApiError();
  const [creating, setCreating] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [verifying, setVerifying] = React.useState<StaleBrand | null>(null);
  const [locationsFor, setLocationsFor] = React.useState<{ id: string; label: string } | null>(null);

  async function reverify(brand: StaleBrand) {
    try {
      await verify.mutateAsync(brand.id);
      toast.success(
        t('brands.reverifiedTitle', {
          defaultValue: '{name} re-verified.',
          name: brand.name_en ?? brand.slug,
        })
      );
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    } finally {
      setVerifying(null);
    }
  }

  const staleColumns: readonly DataColumn<StaleBrand>[] = [
    {
      id: 'name',
      header: t('brands.col.name', { defaultValue: 'Name' }),
      cell: (r) => (
        <div className="space-y-0.5">
          <p className="font-medium">{r.name_en ?? r.slug}</p>
          <p className="font-mono text-xs text-[color:var(--color-fg-muted)]">
            <span dir="ltr">{r.slug}</span>
          </p>
        </div>
      ),
      sortBy: (r) => r.name_en ?? r.slug,
    },
    {
      id: 'last',
      header: t('brands.col.lastVerified', { defaultValue: 'Last verified' }),
      cell: (r) =>
        r.last_verified_at ? (
          <DateTime value={r.last_verified_at} />
        ) : (
          <Badge tone="warn">{t('brands.neverVerified', { defaultValue: 'Never verified' })}</Badge>
        ),
      sortBy: (r) => (r.last_verified_at ? new Date(r.last_verified_at) : new Date(0)),
    },
    {
      id: 'sla',
      header: t('brands.col.sla', { defaultValue: 'SLA' }),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs">
          {t('brands.slaDays', { defaultValue: '{n}d', n: r.verification_sla_days })}
        </span>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('brands.col.actions', { defaultValue: 'Actions' })}</span>,
      align: 'end',
      cell: (r) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              setLocationsFor({ id: r.id, label: r.name_en ?? r.slug })
            }
          >
            <MapPin className="size-4" aria-hidden />
            <span className="sr-only md:not-sr-only md:text-xs">
              {t('brandLocations.title', { defaultValue: 'Locations' })}
            </span>
          </Button>
          <Button
            size="sm"
            onClick={() => setVerifying(r)}
            disabled={verify.isPending}
          >
            <ShieldCheck className="size-4" aria-hidden />
            {t('brands.reverify', { defaultValue: 'Re-verify' })}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('brands.title', { defaultValue: 'Brand directory' })}
          </h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
            {t('brands.subtitle', {
              defaultValue:
                'Curated plus-size + modest brand directory. Re-verify brands every 90 days; the stale queue surfaces breaches. Bulk-import seeds the directory from CSV.',
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setImporting(true)}>
            <Upload className="size-4" aria-hidden />
            {t('brands.bulkImport', { defaultValue: 'Bulk import' })}
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            {t('brands.new', { defaultValue: 'New brand' })}
          </Button>
        </div>
      </header>

      <Tabs defaultValue="stale">
        <TabsList>
          <TabsTrigger value="stale">{t('brands.tabs.stale', { defaultValue: 'Stale queue' })}</TabsTrigger>
          <TabsTrigger value="proposals">
            {t('brands.tabs.proposals', { defaultValue: 'Proposals' })}
          </TabsTrigger>
          <TabsTrigger value="reviews">
            {t('brands.tabs.reviews', { defaultValue: 'Reviews' })}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="stale">
          <Card>
            <CardContent className="pt-6">
              <DataTable<StaleBrand>
                data={stale.data ?? []}
                columns={staleColumns}
                rowKey={(r) => r.id}
                loading={stale.isLoading}
                error={stale.error ? resolveError(stale.error) : null}
                empty={{
                  title: t('brands.staleEmpty', { defaultValue: 'No stale brands.' }),
                  body: t('brands.staleEmptyBody', {
                    defaultValue: 'Every published brand is fresh within its 90-day SLA.',
                  }),
                }}
                initialSort={{ id: 'last', direction: 'asc' }}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="proposals">
          <Card>
            <CardContent className="pt-6">
              <p className="mb-3 text-sm text-[color:var(--color-fg-muted)]">
                {t('brands.proposalsHint', {
                  defaultValue:
                    'No list endpoint is documented for proposals. Decide proposals by ID below.',
                })}
              </p>
              <ProposalDecideForm />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="reviews">
          <Card>
            <CardContent className="pt-6">
              <p className="mb-3 text-sm text-[color:var(--color-fg-muted)]">
                {t('brands.reviewsHint', {
                  defaultValue:
                    'No list endpoint is documented. Moderate community-submitted reviews by ID below.',
                })}
              </p>
              <ReviewModerateForm />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('brands.newTitle', { defaultValue: 'New brand' })}</DialogTitle>
            <DialogDescription>
              {t('brands.newBody', {
                defaultValue:
                  'Idempotent on slug. Re-saving the same slug updates the existing record. Publishing stamps verification metadata.',
              })}
            </DialogDescription>
          </DialogHeader>
          <BrandForm
            onSaved={(id) => {
              setCreating(false);
              setLocationsFor({ id, label: t('brands.justSaved', { defaultValue: 'New brand' }) });
            }}
            onCancel={() => setCreating(false)}
          />
        </DialogContent>
      </Dialog>

      <CsvImportDialog
        open={importing}
        onOpenChange={setImporting}
        rowSchema={brandUpsertSchema}
        maxRows={BRAND_BULK_ROW_CAP}
        maxBytes={BRAND_BULK_BODY_BYTES_CAP}
        title={t('brands.bulkImportTitle', { defaultValue: 'Bulk import brands' })}
        description={t('brands.bulkImportBody', {
          defaultValue:
            'CSV header should match the brand upsert fields. Max 500 rows, 2 MiB body. Invalid rows are silently skipped server-side — fix here first.',
        })}
        onSubmit={async (rows) => bulk.mutateAsync(rows)}
        guardFields={(row) => ({
          name_en: row.name_en,
          name_ar: row.name_ar,
          name_eg_ar: row.name_eg_ar,
          bio_en: row.bio_en,
          bio_ar: row.bio_ar,
        })}
      />

      <Dialog open={Boolean(locationsFor)} onOpenChange={(o) => !o && setLocationsFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('brandLocations.dialogTitle', {
                defaultValue: 'Add location: {label}',
                label: locationsFor?.label ?? '',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('brandLocations.dialogBody', {
                defaultValue: 'Locations stack — add as many as the brand operates.',
              })}
            </DialogDescription>
          </DialogHeader>
          {locationsFor ? <BrandLocationForm brandId={locationsFor.id} /> : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(verifying)}
        onOpenChange={(o) => !o && setVerifying(null)}
        title={t('brands.reverifyConfirmTitle', {
          defaultValue: 'Re-verify {name}?',
          name: verifying?.name_en ?? verifying?.slug ?? '',
        })}
        description={t('brands.reverifyConfirmBody', {
          defaultValue:
            'Stamps last_verified_at = now() with you as verifier. Use only after confirming the brand still trades.',
        })}
        confirmLabel={t('brands.reverify', { defaultValue: 'Re-verify' })}
        loading={verify.isPending}
        onConfirm={() => (verifying ? reverify(verifying) : Promise.resolve())}
      />
    </div>
  );
}

function ProposalDecideForm() {
  const { t } = useTranslation('phase2');
  const decide = useDecideBrandProposal();
  const resolveError = useApiError();
  const [id, setId] = React.useState('');
  const [decision, setDecision] = React.useState<'accepted' | 'rejected'>('accepted');
  const [notes, setNotes] = React.useState('');
  const [error, setError] = React.useState<string | undefined>(undefined);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!UUID.test(id.trim())) {
      setError(t('brands.proposalInvalidId', { defaultValue: 'Proposal id must be a UUID.' }));
      return;
    }
    setError(undefined);
    try {
      await decide.mutateAsync({
        proposalId: id.trim(),
        input: { state: decision, ...(notes ? { notes } : {}) },
      });
      toast.success(t('brands.proposalDecided', { defaultValue: 'Proposal decided.' }));
      setId('');
      setNotes('');
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label htmlFor="proposal-id">{t('brands.proposalId', { defaultValue: 'Proposal id' })}</Label>
        <Input
          id="proposal-id"
          dir="ltr"
          className="font-mono"
          value={id}
          onChange={(e) => setId(e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          aria-invalid={Boolean(error)}
        />
        {error ? <p role="alert" className="mt-1 text-xs text-[color:var(--color-danger)]">{error}</p> : null}
      </div>
      <div role="radiogroup" className="flex gap-2">
        {(['accepted', 'rejected'] as const).map((d) => (
          <label
            key={d}
            className={`cursor-pointer rounded-[var(--radius-pill)] border border-[color:var(--color-border)] px-3 py-1 text-xs font-medium ${
              decision === d
                ? 'bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]'
                : 'bg-[color:var(--color-surface)]'
            }`}
          >
            <input
              type="radio"
              className="sr-only"
              checked={decision === d}
              onChange={() => setDecision(d)}
              name="proposal-decision"
            />
            {t(`brands.proposalDecisions.${d}`, { defaultValue: d })}
          </label>
        ))}
      </div>
      <div>
        <Label htmlFor="proposal-notes">{t('brands.proposalNotes', { defaultValue: 'Notes' })}</Label>
        <textarea
          id="proposal-notes"
          rows={3}
          maxLength={2000}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="block w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] focus-visible:ring-offset-2"
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        <AuditChip />
        <Button type="submit" loading={decide.isPending}>
          {t('brands.proposalDecideCta', { defaultValue: 'Decide' })}
        </Button>
      </div>
    </form>
  );
}

function ReviewModerateForm() {
  const { t } = useTranslation('phase2');
  const moderate = useModerateBrandReview();
  const resolveError = useApiError();
  const [id, setId] = React.useState('');
  const [decision, setDecision] = React.useState<'published' | 'rejected'>('published');
  const [error, setError] = React.useState<string | undefined>(undefined);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!UUID.test(id.trim())) {
      setError(t('brands.reviewInvalidId', { defaultValue: 'Review id must be a UUID.' }));
      return;
    }
    setError(undefined);
    try {
      await moderate.mutateAsync({ reviewId: id.trim(), input: { state: decision } });
      toast.success(t('brands.reviewModerated', { defaultValue: 'Review moderated.' }));
      setId('');
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label htmlFor="review-id">{t('brands.reviewId', { defaultValue: 'Review id' })}</Label>
        <Input
          id="review-id"
          dir="ltr"
          className="font-mono"
          value={id}
          onChange={(e) => setId(e.target.value)}
          aria-invalid={Boolean(error)}
        />
        {error ? <p role="alert" className="mt-1 text-xs text-[color:var(--color-danger)]">{error}</p> : null}
      </div>
      <div role="radiogroup" className="flex gap-2">
        {(['published', 'rejected'] as const).map((d) => (
          <label
            key={d}
            className={`cursor-pointer rounded-[var(--radius-pill)] border border-[color:var(--color-border)] px-3 py-1 text-xs font-medium ${
              decision === d
                ? 'bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]'
                : 'bg-[color:var(--color-surface)]'
            }`}
          >
            <input
              type="radio"
              className="sr-only"
              checked={decision === d}
              onChange={() => setDecision(d)}
              name="review-decision"
            />
            {t(`brands.reviewDecisions.${d}`, { defaultValue: d })}
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2">
        <AuditChip />
        <Button type="submit" loading={moderate.isPending}>
          {t('brands.reviewModerateCta', { defaultValue: 'Moderate' })}
        </Button>
      </div>
    </form>
  );
}
